import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { contractsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import ContractDocument from '../components/ContractDocument';
import SignatureInput from '../components/SignatureInput';
import { CheckCircle2, AlertTriangle, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';

const ContractSigningPage = () => {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [signerName, setSignerName] = useState('');
  const [signerDesignation, setSignerDesignation] = useState('');
  const [witnessName, setWitnessName] = useState('');
  const [signerSignatureData, setSignerSignatureData] = useState('');
  const [witnessSignatureData, setWitnessSignatureData] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await contractsAPI.getPublic(token);
        setContract(res.data);
        setFieldValues(res.data.field_values || {});
        setSignerName(res.data.field_values?.signer_name || '');
        setSignerDesignation(res.data.field_values?.signer_designation || '');
        setWitnessName(res.data.field_values?.witness_name || '');
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load contract');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Get the editable fields (restaurant_fills + signatory_fills) from template
  const editableFields = (contract?.template_snapshot?.fields || []).filter(
    (f) => f.source === 'restaurant_fills'
  );

  const handleSubmit = async () => {
    const requireWitness = contract?.template_snapshot?.require_witness !== false;
    if (!signerName.trim()) {
      toast.error('Signatory name is required');
      return;
    }
    if (requireWitness && !witnessName.trim()) {
      toast.error('Witness name is required');
      return;
    }
    if (!signerSignatureData) {
      toast.error('Signatory signature is required');
      return;
    }
    if (requireWitness && !witnessSignatureData) {
      toast.error('Witness signature is required');
      return;
    }
    // Validate required restaurant_fills
    for (const f of editableFields) {
      if (f.required && !(fieldValues[f.key] || '').toString().trim()) {
        toast.error(`Please fill in: ${f.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await contractsAPI.signPublic(token, {
        field_values: fieldValues,
        signer_name: signerName.trim(),
        signer_designation: signerDesignation.trim(),
        signer_signature_data: signerSignatureData,
        witness_name: requireWitness ? witnessName.trim() : '',
        witness_signature_data: requireWitness ? witnessSignatureData : '',
        signed_location: (fieldValues.signed_location || '').trim(),
      });
      toast.success('Contract signed successfully');
      // Reload to show signed state
      const fresh = await contractsAPI.getPublic(token);
      setContract(fresh.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-ica-navy" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-ica-navy mb-2">Unable to load contract</h1>
            <p className="text-gray-600 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSigned = contract.status === 'signed';
  // Default to TRUE when key absent — older snapshots predate the flag.
  // Placement-category contracts explicitly set this to false at seed time.
  const requireWitness = contract.template_snapshot?.require_witness !== false;
  const signerRoleLabel = contract.template_snapshot?.signer_role_label || 'Signatory';
  const templateName = contract.template_snapshot?.name || 'ICA Contract';
  const signatures = {
    signer: contract.signer_signature_data,
    witness: contract.witness_signature_data,
    ica_signer: contract.ica_signature_data,
  };

  // Live preview signatures (only when not yet signed)
  const liveSignatures = isSigned ? signatures : {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-ica-navy text-white px-6 py-4 no-print">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-lg font-bold">{templateName}</h1>
            <p className="text-xs text-gray-300">{contract.restaurant_name} · {contract.year_of_study}</p>
          </div>
          {isSigned && (
            <Button
              data-testid="print-contract-btn"
              variant="outline"
              onClick={() => window.print()}
              className="text-ica-navy bg-white hover:bg-gray-100"
              size="sm"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              Print / Save PDF
            </Button>
          )}
        </div>
      </div>

      {isSigned && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 no-print">
          <div className="max-w-4xl mx-auto flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 className="w-4 h-4" />
            <span>Signed on {new Date(contract.signed_at).toLocaleString()} by <strong>{contract.signer_name}</strong>. This document is now legally binding.</span>
          </div>
        </div>
      )}

      <div className="contract-print-area py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <ContractDocument
            bodyHtml={contract.template_snapshot?.body_html || ''}
            fieldValues={isSigned ? contract.field_values : { ...fieldValues, signer_name: signerName, signer_designation: signerDesignation, witness_name: witnessName }}
            signatures={liveSignatures}
            logoUrl={contract.logo_url}
            senderBlockHtml={contract.template_snapshot?.sender_block_html}
          />
        </div>
      </div>

      {!isSigned && (
        <div className="bg-white border-t border-gray-200 px-4 py-6 no-print">
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h2 className="font-heading text-xl font-bold text-ica-navy mb-1">Complete the agreement</h2>
              <p className="text-sm text-gray-500">Fill in the required fields, then sign and have a witness sign below.</p>
            </div>

            {editableFields.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {editableFields.map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}{f.required ? ' *' : ''}</Label>
                    <Input
                      data-testid={`field-${f.key}`}
                      value={fieldValues[f.key] || ''}
                      onChange={(e) => setFieldValues({ ...fieldValues, [f.key]: e.target.value })}
                      placeholder={f.default_value || ''}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className={`grid grid-cols-1 ${requireWitness ? 'md:grid-cols-2' : ''} gap-6`}>
              <SignatureInput
                title={signerRoleLabel}
                name={signerName}
                onNameChange={setSignerName}
                designation={signerDesignation}
                onDesignationChange={setSignerDesignation}
                showDesignation={requireWitness}
                testIdPrefix="signer"
                onChange={setSignerSignatureData}
              />
              {requireWitness && (
                <SignatureInput
                  title="Witness"
                  name={witnessName}
                  onNameChange={setWitnessName}
                  testIdPrefix="witness"
                  onChange={setWitnessSignatureData}
                />
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                data-testid="submit-contract-btn"
                onClick={handleSubmit}
                disabled={submitting}
                
              >
                {submitting ? 'Submitting…' : 'Submit & Sign'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractSigningPage;
